import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

interface MedicinalProperty {
  _id: string;
  count: number;
}

const MedicinalPropertiesSidebar = () => {
  const [properties, setProperties] = useState<MedicinalProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        console.log('Fetching medicinal properties...');
        const response = await axios.get(`${API_BASE_URL}/api/plants/medicinal-properties`);
        console.log('Received response:', response.data);
        setProperties(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching medicinal properties:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          config: {
            url: err.config?.url,
            method: err.config?.method,
            headers: err.config?.headers
          }
        });
        setError(`Failed to load medicinal properties: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  const handlePropertyClick = (property: string) => {
    navigate(`/botanicals?medicinal=${encodeURIComponent(property)}`);
  };

  return (
    <div className="mt-8 pt-6 border-t border-white/10">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-4 text-sm font-medium text-white/80 hover:text-white transition-colors"
      >
        <div className="flex items-center space-x-2">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            strokeWidth={1.5} 
            stroke="currentColor" 
            className="h-4 w-4"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" 
            />
          </svg>
          <span>Medicinal Properties</span>
        </div>
        <svg 
          className={`h-4 w-4 transform transition-transform ${expanded ? 'rotate-180' : ''}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {expanded && (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-6 bg-white/10 rounded animate-pulse"></div>
              ))}
            </div>
          ) : error ? (
            <p className="text-xs text-red-400">{error}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {properties.map((prop) => (
                <button
                  key={prop._id}
                  onClick={() => handlePropertyClick(prop._id)}
                  className="text-xs bg-white/10 hover:bg-white/20 text-white/80 hover:text-white px-2 py-1 rounded transition-colors"
                  title={`${prop.count} plants with this property`}
                >
                  {prop._id} <span className="text-white/60">({prop.count})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MedicinalPropertiesSidebar;
